import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';

import {buildControlFlowEdge, buildDataFlowEdge} from '../../buildStaticDataFromFlowchartConfigGraphQL';
import {FlowchartStaticEdgeData, StaticTemplateFlowchart} from '../../processFlowchartConfigEditorConstants';
import {convertTaskDefinitionVersionToSpec} from '../../processFlowchartConfigEditorNetworkUtils';
import {TaskSchemaStaticNodeData} from '../../processFlowchartConfigEditorNodeTypes';
import {createStaticNodeDataFromTaskDefinitionVersionSpec} from '../../processFlowchartConfigEditorUtils';
import {parseFieldPairKey} from '../../types/transition/transitionKeyUtils';
import {Transition} from '../../types/transition/TransitionTypes';
import {ConfigurableTaskDefinitionGQL} from '../../useAllSchemasById';
import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';

export interface BuildStaticFlowchartFromTransitionArgs {
  transition: Transition;
  ptdvIdToTaskSchemaId: Record<string, string>;
  taskDefinitionsForSidepanel: Array<ConfigurableTaskDefinitionGQL>;
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
}

class BuildStaticFlowchartFromTransitionRunner {
  private readonly transition: Transition;
  private readonly ptdvIdToTaskSchemaId: Record<string, string>;
  private readonly rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  private readonly taskSchemaIdToDefinition: Map<string, ConfigurableTaskDefinitionGQL>;

  private nodesStaticData: Array<TaskSchemaStaticNodeData>;
  private edgesStaticData: Array<FlowchartStaticEdgeData>;
  private ptdvIdToNodeClientId: Record<string, string>;

  constructor(args: BuildStaticFlowchartFromTransitionArgs) {
    this.transition = args.transition;
    this.ptdvIdToTaskSchemaId = args.ptdvIdToTaskSchemaId;
    this.rootTaskSchema = args.rootTaskSchema;
    this.taskSchemaIdToDefinition = new Map(
      args.taskDefinitionsForSidepanel.map((def) => [def.latestVersion.processTaskSchema.id, def])
    );

    this.nodesStaticData = [];
    this.edgesStaticData = [];
    this.ptdvIdToNodeClientId = {};
  }

  run(): StaticTemplateFlowchart {
    this.buildNodes();
    this.buildControlFlowEdges();
    this.buildDataFlowEdges();

    return {
      nodesStaticData: this.nodesStaticData,
      edgesStaticData: this.edgesStaticData,
    };
  }

  private buildNodes(): void {
    for (const sourcePTDVId of this.transition.sourcePTDVIds) {
      this.buildNodeForPTDV(sourcePTDVId, true);
    }
    this.buildNodeForPTDV(this.transition.targetPTDVId, false);
  }

  private buildNodeForPTDV(ptdvId: string, isSource: boolean): void {
    if (this.ptdvIdToNodeClientId[ptdvId] != null) {
      return;
    }

    const taskSchemaId = this.ptdvIdToTaskSchemaId[ptdvId];
    assert(taskSchemaId != null, `No task schema found for PTDV ${ptdvId}`);

    const definition = this.taskSchemaIdToDefinition.get(taskSchemaId);
    assert(definition != null, `No task definition found for task schema ${taskSchemaId}`);

    // Transitions are based on PTDV IDs, not node IDs
    // Create temporary node client ID for rendering within the transition modal
    // On save, we only pass back an updated transition with PTDV IDs
    const nodeClientId = uuidv4();
    this.ptdvIdToNodeClientId[ptdvId] = nodeClientId;

    const processTaskSchema = definition.latestVersion.processTaskSchema;
    const spec = convertTaskDefinitionVersionToSpec(definition.latestVersion, processTaskSchema);
    const nodeDetailsType = this.getNodeDetailsType(taskSchemaId, isSource);

    this.nodesStaticData.push(
      createStaticNodeDataFromTaskDefinitionVersionSpec({
        nodeClientId,
        taskDefinitionVersionSpec: spec,
        taskSchemaId,
        nodeDetailsType,
      })
    );
  }

  private getNodeDetailsType(taskSchemaId: string, isSource: boolean): ProcessFlowchartNodeDetailsType {
    if (taskSchemaId === this.rootTaskSchema.id) {
      return isSource
        ? ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS
        : ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS;
    }
    return ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS;
  }

  private buildControlFlowEdges(): void {
    const targetNodeId = this.ptdvIdToNodeClientId[this.transition.targetPTDVId];
    assert(targetNodeId != null, `No node found for target PTDV ${this.transition.targetPTDVId}`);

    for (const sourcePTDVId of this.transition.sourcePTDVIds) {
      const sourceNodeId = this.ptdvIdToNodeClientId[sourcePTDVId];
      assert(sourceNodeId != null, `No node found for source PTDV ${sourcePTDVId}`);
      this.edgesStaticData.push(buildControlFlowEdge({sourceNodeId, targetNodeId}));
    }
  }

  private buildDataFlowEdges(): void {
    for (const fieldPairKey of Object.keys(this.transition.customMappingDataByFieldPair)) {
      const parsed = parseFieldPairKey(fieldPairKey);
      assert(parsed != null, `Could not parse field pair key: ${fieldPairKey}`);

      const sourceNodeId = this.ptdvIdToNodeClientId[parsed.sourcePTDVId];
      const targetNodeId = this.ptdvIdToNodeClientId[parsed.targetPTDVId];
      assert(
        sourceNodeId != null && targetNodeId != null,
        `Missing node for data flow edge: source=${parsed.sourcePTDVId} target=${parsed.targetPTDVId}`
      );

      this.edgesStaticData.push(
        buildDataFlowEdge({
          sourceNodeId,
          targetNodeId,
          fromFieldId: parsed.sourceFieldClientId,
          fromFieldSectionType: parsed.sourceSectionType,
          toFieldId: parsed.targetFieldClientId,
          toFieldSectionType: parsed.targetSectionType,
        })
      );
    }
  }
}

export default function buildStaticFlowchartFromTransition(
  args: BuildStaticFlowchartFromTransitionArgs
): StaticTemplateFlowchart {
  const runner = new BuildStaticFlowchartFromTransitionRunner(args);
  return runner.run();
}
